import * as React from 'react';
import { useState, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { PieChart, Pie } from 'recharts';
import Card from '../../sharedComponents/core/Cards/Card';
import { Theme } from 'helpers/theme';

const ELEVATED_SHADOW =
  '0 70px 20px 0 rgba(209, 209, 209, 0.00), 0 45px 18px 0 rgba(209, 209, 209, 0.01), 0 25px 15px 0 rgba(209, 209, 209, 0.05), 0 11px 11px 0 rgba(209, 209, 209, 0.09), 0 3px 6px 0 rgba(209, 209, 209, 0.10)';

const useStyles = createUseStyles((theme: Theme) => ({
  cardRoot: {
    boxShadow: ELEVATED_SHADOW
  },
  card: {
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: 12
  },
  iconContainer: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#F4F5F7',
    borderRadius: 8,
    fontSize: 14
  },
  titleBlock: {},
  title: {
    fontWeight: 800,
    fontSize: 14,
    color: theme.colors.primaryBlack
  },
  description: {
    fontSize: 12,
    color: '#6A728F',
    marginTop: 2
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center'
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: 8,
    userSelect: 'none'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'default'
  },
  legendItemClickable: {
    cursor: 'pointer'
  },
  legendBar: {
    width: 4,
    height: 40,
    borderRadius: 2,
    flexShrink: 0
  },
  legendLabel: {
    fontSize: 11,
    color: '#6A728F'
  },
  legendValue: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.colors.primaryBlack,
    transition: 'opacity 0.5s'
  },
  chartWrapper: {
    position: 'relative',
    paddingLeft: 16,
    paddingRight: 16
  },
  centerText: {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 800,
    color: theme.colors.primaryBlack,
    transition: 'opacity 0.5s'
  }
}));

type DataPoint = {
  value: number;
  colour: string;
  key: string;
  onClick?: () => void;
};

type Props = {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  data: [DataPoint, DataPoint, ...DataPoint[]];
  percentage?: boolean;
  className?: string;
};

export default function GaugeCard({
  title,
  description,
  icon,
  data,
  percentage = false,
  className
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const total = data.reduce((sum, d) => sum + d.value, 0);

  const gaugeData = data
    .map((d) => ({
      v: total > 0 ? d.value / total : 1 / data.length,
      fill: d.colour,
      onClick: d.onClick
    }))
    .filter((d) => d.v > 0);

  const clickable = gaugeData.some((d) => d.onClick);

  const centerText = percentage
    ? `${Math.round(total > 0 ? (data[0].value / total) * 100 : 0)}%`
    : data.length === 2
    ? String(data[0].value)
    : String(total);

  return (
    <Card
      className={classNames(classes.cardRoot, className)}
      padding="none"
    >
      <div className={classes.card}>
        {title && (
          <div className={classes.header}>
            {icon && <div className={classes.iconContainer}>{icon}</div>}
            <div className={classes.titleBlock}>
              <div className={classes.title}>{title}</div>
              {description && (
                <div className={classes.description}>{description}</div>
              )}
            </div>
          </div>
        )}
        <div className={classes.content}>
          <div className={classes.legend}>
            {data.map((datapoint, i) => (
              <div
                key={i}
                className={`${classes.legendItem} ${
                  datapoint.onClick ? classes.legendItemClickable : ''
                }`}
                onClick={datapoint.onClick}
              >
                <div
                  className={classes.legendBar}
                  style={{ backgroundColor: datapoint.colour }}
                />
                <div>
                  <div className={classes.legendLabel}>{datapoint.key}</div>
                  <div
                    className={classes.legendValue}
                    style={{ opacity: visible ? 1 : 0 }}
                  >
                    {datapoint.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={classes.chartWrapper}>
            <PieChart
              width={180}
              height={100}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <Pie
                data={gaugeData}
                cx={90}
                cy={90}
                startAngle={180}
                endAngle={0}
                innerRadius={52}
                outerRadius={82}
                dataKey="v"
                strokeWidth={0}
                cornerRadius={6}
                paddingAngle={gaugeData.length > 1 ? 3 : 0}
                cursor={clickable ? 'pointer' : undefined}
                onClick={(entry: any) => {
                  const handler = entry?.payload?.onClick ?? entry?.onClick;
                  if (handler) handler();
                }}
              />
            </PieChart>
            <p
              className={classes.centerText}
              style={{ top: 48, opacity: visible ? 1 : 0 }}
            >
              {centerText}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
